/**
 * Copyright 2015 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var _a, _b, _c;
import { __assign, __extends } from "tslib";
import { alDefineObjectProperties, alNewObject, alToBoolean, alToNumber, alToString } from '../runtime';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Function } from '../runtime/AVM1Function';
import { AVM1Point, toAS3Point } from './AVM1Point';
import { AVM1BitmapData, toAS3BitmapData } from './AVM1BitmapData';
import { AVM1ArrayNative } from '../natives';
import { FilterType as TimelineFilterType } from '@awayfl/swf-loader';
var NonTimelineFilterType;
(function (NonTimelineFilterType) {
    NonTimelineFilterType[NonTimelineFilterType["DISPLACEMENT"] = 8] = "DISPLACEMENT";
})(NonTimelineFilterType || (NonTimelineFilterType = {}));
export var ExtendsFilterType = __assign(__assign({}, TimelineFilterType), NonTimelineFilterType);
var FILTER_TYPE_NAME_MAP = (_a = {},
    _a[ExtendsFilterType.DROPSHADOW] = 'DropShadowFilter',
    _a[ExtendsFilterType.GLOW] = 'GlowFilter',
    _a[ExtendsFilterType.BEVEL] = 'BevelFilter',
    _a[ExtendsFilterType.GRADIENTGLOW] = 'GradientGlowFilter',
    _a[ExtendsFilterType.GRADIENTBEVEL] = 'GradientBevelFilter',
    _a[ExtendsFilterType.BLUR] = 'BlurFilter',
    _a[ExtendsFilterType.CONVOLUTION] = 'ConvolutionFilter',
    _a[ExtendsFilterType.COLORMATRIX] = 'ColorMatrixFilter',
    // displacement can be created from timeline
    _a[ExtendsFilterType.DISPLACEMENT] = 'DisplacementMapFilter',
    _a);
var FILTER_TO_SIMPLE_NAME = (_b = {},
    _b[ExtendsFilterType.DROPSHADOW] = 'dropShadow',
    _b[ExtendsFilterType.GLOW] = 'glow',
    _b[ExtendsFilterType.BEVEL] = 'bevel',
    _b[ExtendsFilterType.GRADIENTGLOW] = 'gradientGlow',
    _b[ExtendsFilterType.GRADIENTBEVEL] = 'bevel',
    _b[ExtendsFilterType.BLUR] = 'blur',
    _b[ExtendsFilterType.CONVOLUTION] = 'convolution',
    _b[ExtendsFilterType.COLORMATRIX] = 'colorMatrix',
    // displacement can be created from timeline
    _b[ExtendsFilterType.DISPLACEMENT] = 'displacement',
    _b);
var FILL_FILTER_FROM_TIMELINE = (_c = {},
    _c[ExtendsFilterType.DROPSHADOW] = function (obj) {
        var color = obj.colors[0] >>> 8;
        var alpha = (obj.colors[0] & 0xff) / 0xff;
        // obj.angle is represented in radians, the api needs degrees
        var angle = obj.angle * 180 / Math.PI;
        // obj.compositeSource maps to !hideObject
        var hideObject = !obj.compositeSource;
        return {
            distance: obj.distance,
            angle: angle,
            color: color,
            alpha: alpha,
            blurX: obj.blurX,
            blurY: obj.blurY,
            strength: obj.strength,
            quality: obj.quality,
            inner: obj.inner,
            knockout: obj.knockout,
            hideObject: hideObject
        };
    },
    _c[ExtendsFilterType.GLOW] = function (obj) {
        // obj.colors is an array of RGBA colors.
        var color = obj.colors[0] >>> 8;
        var alpha = (obj.colors[0] & 0xff) / 0xff;
        return {
            color: color,
            alpha: alpha,
            blurX: obj.blurX,
            blurY: obj.blurY,
            strength: obj.strength,
            quality: obj.quality,
            inner: obj.inner,
            knockout: obj.knockout
        };
    },
    _c[ExtendsFilterType.BEVEL] = function (obj) {
        // obj.colors is an array of RGBA colors.
        // Here it contains exactly two color objects (spec might state it differently):
        //  - first maps to highlightColor and highlightAlpha;
        //  - second maps to shadowColor and shadowAlpha;
        var highlightColor = obj.colors[0] >>> 8;
        var highlightAlpha = (obj.colors[0] & 0xff) / 0xff;
        var shadowColor = obj.colors[1] >>> 8;
        var shadowAlpha = (obj.colors[1] & 0xff) / 0xff;
        // type is derived from obj.onTop and obj.innerShadow
        // obj.onTop true: type is FULL
        // obj.inner true: type is INNER
        // neither true: type is OUTER
        var type = "outer" /* OUTER */;
        if (obj.onTop) {
            type = "full" /* FULL */;
        }
        else if (obj.inner) {
            type = "inner" /* INNER */;
        }
        // obj.angle is represented in radians, the api needs degrees
        var angle = obj.angle * 180 / Math.PI;
        return {
            distance: obj.distance,
            angle: angle,
            highlightColor: highlightColor,
            highlightAlpha: highlightAlpha,
            shadowColor: shadowColor,
            shadowAlpha: shadowAlpha,
            blurX: obj.blurX,
            blurY: obj.blurY,
            strength: obj.strength,
            quality: obj.quality,
            type: type,
            knockout: obj.knockout
        };
    },
    _c[ExtendsFilterType.GRADIENTGLOW] = function (obj) {
        return null;
    },
    _c[ExtendsFilterType.GRADIENTBEVEL] = function (data) {
        return null;
    },
    _c[ExtendsFilterType.BLUR] = function (obj) {
        return null;
    },
    _c[ExtendsFilterType.CONVOLUTION] = function (obj) {
        return null;
    },
    _c[ExtendsFilterType.COLORMATRIX] = function (data) {
        return null;
    },
    _c);
// Base class/function for all AVM1 filters.
var AVM1BitmapFilterFunction = /** @class */ (function (_super) {
    __extends(AVM1BitmapFilterFunction, _super);
    function AVM1BitmapFilterFunction(context) {
        var _this = _super.call(this, context) || this;
        _this.alSetOwnPrototypeProperty(new AVM1BitmapFilterPrototype(context, _this));
        return _this;
    }
    AVM1BitmapFilterFunction.prototype.alConstruct = function (args) {
        var obj = new AVM1Object(this.context);
        obj.alPrototype = this.alGetPrototypeProperty();
        return obj;
    };
    return AVM1BitmapFilterFunction;
}(AVM1Function));
var AVM1BitmapFilterPrototype = /** @class */ (function (_super) {
    __extends(AVM1BitmapFilterPrototype, _super);
    function AVM1BitmapFilterPrototype(context, fn) {
        var _this = _super.call(this, context) || this;
        alDefineObjectProperties(_this, {
            constructor: {
                value: fn,
                writable: true
            },
            clone: {
                value: _this.clone,
                writable: true
            }
        });
        return _this;
    }
    AVM1BitmapFilterPrototype.prototype.clone = function () {
        var obj = new AVM1Object(this.context);
        obj.alPrototype = this.alGetPrototypeProperty();
        return obj;
    };
    return AVM1BitmapFilterPrototype;
}(AVM1Object));
// Automates creation of the AVM1 filter classes.
function createFilterClass(context, filtersObj, base, type, fields) {
    var name = FILTER_TYPE_NAME_MAP[type];
    // Simple constructor for the class function.
    function construct(args) {
        var as2Object = new AVM1Object(context);
        as2Object.alPrototype = wrappedProto;
        if (args) {
            for (var i = 0; i < args.length; i++) {
                as2Object.alPut(fields[i << 1], args[i]);
            }
        }
        return as2Object;
    }
    function clone() {
        var as2Object = new AVM1Object(context);
        as2Object.alPrototype = wrappedProto;
        for (var i = 0; i < fields.length; i += 2) {
            as2Object.alPut(fields[i], this.alGet(fields[i]));
        }
        return as2Object;
    }
    function toAS3Filter(as2Object) {
        var awayFilterModel = {
            filterName: name,
            filterType: type,
        };
        // Just copying all defined properties.
        for (var i = 0; i < fields.length; i += 2) {
            var as2Value = as2Object.alGet(fields[i]);
            if (as2Value === undefined) {
                continue; // skipping undefined
            }
            awayFilterModel[fields[i]] = convertToAS3Field(context, as2Value, fields[i + 1]);
        }
        return awayFilterModel;
    }
    function fromAS3Filter(awayObject) {
        var as2Object = new AVM1Object(context);
        as2Object.alPrototype = wrappedProto;
        for (var i = 0; i < fields.length; i += 2) {
            as2Object.alPut(fields[i], convertFromAS3Field(context, awayObject[fields[i]], fields[i + 1]));
        }
        return as2Object;
    }
    function fromTimelineFilter(data) {
        var CONVERTER = FILL_FILTER_FROM_TIMELINE[data.type];
        var model = CONVERTER ? CONVERTER(data) : null;
        if (!model) {
            console.warn('[AVM1 FILTER] Timeline filter converter not implemented for:', data);
            return undefined;
        }
        return fromAS3Filter(model);
    }
    // Creates new prototype object and function for the class.
    var proto = base.alGetPrototypeProperty();
    var wrappedProto = Object.create(AVM1BitmapFilterPrototype.prototype);
    AVM1Object.call(wrappedProto, context);
    wrappedProto.alPrototype = proto;
    var wrapped = Object.create(AVM1BitmapFilterFunction.prototype);
    AVM1Function.call(wrapped, context);
    wrapped.alSetOwnPrototypeProperty(wrappedProto);
    wrapped.alConstruct = construct;
    alDefineObjectProperties(wrappedProto, {
        constructor: {
            value: wrapped,
            writable: true
        },
        clone: {
            value: clone,
            writable: true
        }
    });
    //... and also attaches conversion utility.
    wrappedProto.asFilterConverter = {
        toAS3Filter: toAS3Filter,
        fromAS3Filter: fromAS3Filter,
        fromTimelineFilter: fromTimelineFilter,
        getAS3Class: null
    };
    filtersObj.alPut(name, wrapped);
}
export function createFiltersClasses(context) {
    var filters = alNewObject(context);
    var base = new AVM1BitmapFilterFunction(context);
    filters.alPut('BitmapFilter', base);
    // TODO make field types non-string
    createFilterClass(context, filters, base, ExtendsFilterType.BEVEL, ['distance', 'Number', 'angle', 'Number', 'highlightColor', 'Number',
        'highlightAlpha', 'Number', 'shadowColor', 'Number', 'shadowAlpha', 'Number',
        'blurX', 'Number', 'blurY', 'Number', 'strength', 'Number', 'quality', 'Number',
        'type', 'String', 'knockout', 'Boolean']);
    createFilterClass(context, filters, base, ExtendsFilterType.BLUR, ['blurX', 'Number', 'blurY', 'Number', 'quality', 'Number']);
    createFilterClass(context, filters, base, ExtendsFilterType.COLORMATRIX, ['matrix', 'Numbers']);
    createFilterClass(context, filters, base, ExtendsFilterType.CONVOLUTION, ['matrixX', 'Number', 'matrixY', 'Number', 'matrix', 'Numbers',
        'divisor', 'Number', 'bias', 'Number', 'preserveAlpha', 'Boolean',
        'clamp', 'Boolean', 'color', 'Number', 'alpha', 'Number']);
    createFilterClass(context, filters, base, ExtendsFilterType.DISPLACEMENT, ['mapBitmap', 'BitmapData', 'mapPoint', 'Point', 'componentX', 'Number',
        'componentY', 'Number', 'scaleX', 'Number', 'scaleY', 'Number',
        'mode', 'String', 'color', 'Number', 'alpha', 'Number']);
    createFilterClass(context, filters, base, ExtendsFilterType.DROPSHADOW, ['distance', 'Number', 'angle', 'Number', 'color', 'Number',
        'alpha', 'Number', 'blurX', 'Number', 'blurY', 'Number',
        'strength', 'Number', 'quality', 'Number', 'inner', 'Boolean',
        'knockout', 'Boolean', 'hideObject', 'Boolean']);
    createFilterClass(context, filters, base, ExtendsFilterType.GLOW, ['color', 'Number', 'alpha', 'Number', 'blurX', 'Number', 'blurY', 'Number',
        'strength', 'Number', 'quality', 'Number', 'inner', 'Boolean', 'knockout', 'Boolean']);
    createFilterClass(context, filters, base, ExtendsFilterType.GRADIENTBEVEL, ['distance', 'Number', 'angle', 'Number', 'colors', 'Numbers',
        'alphas', 'Numbers', 'ratios', 'Numbers', 'blurX', 'Number', 'blurY', 'Number',
        'strength', 'Number', 'quality', 'Number', 'type', 'String', 'knockout', 'Boolean']);
    createFilterClass(context, filters, base, ExtendsFilterType.GRADIENTGLOW, ['distance', 'Number', 'angle', 'Number', 'colors', 'Numbers',
        'alphas', 'Numbers', 'ratios', 'Numbers', 'blurX', 'Number', 'blurY', 'Number',
        'strength', 'Number', 'quality', 'Number', 'type', 'String', 'knockout', 'Boolean']);
    return filters;
}
function convertToAS3Field(context, value, type) {
    switch (type) {
        case 'String':
            return alToString(context, value);
        case 'Boolean':
            return alToBoolean(context, value);
        case 'Number':
            return alToNumber(context, value);
        case 'Numbers': {
            var arr = [];
            if (value) {
                for (var i = 0, length_1 = value.alGet('length'); i < length_1; i++) {
                    arr[i] = alToNumber(context, value.alGet(i));
                }
            }
            return arr;
        }
        case 'BitmapData':
            return toAS3BitmapData(value);
        case 'Point':
            return toAS3Point(value);
        default:
            console.warn('Filters', 'Unknown convertFromAS3Field type: ' + type);
    }
}
function convertFromAS3Field(context, value, type) {
    switch (type) {
        case 'String':
        case 'Boolean':
        case 'Number':
            return value;
        case 'Numbers': {
            var arr = [];
            if (value) {
                for (var i = 0, length_2 = value.value.length; i < length_2; i++) {
                    arr[i] = +value.value[i];
                }
            }
            return new AVM1ArrayNative(context, arr);
        }
        case 'BitmapData':
            return AVM1BitmapData.fromAS3BitmapData(context, value);
        case 'Point':
            return AVM1Point.fromAS3Point(context, value);
        default:
            console.warn('Filters', 'Unknown convertFromAS3Field type: ' + type);
    }
}
export function convertToAS3Filter(context, as2Filter) {
    var proto = as2Filter ? as2Filter.alPrototype : null;
    while (proto && !proto.asFilterConverter) {
        proto = proto.alPrototype;
    }
    if (proto) {
        return proto.asFilterConverter.toAS3Filter(as2Filter);
    }
    return undefined;
}
export function convertToAS3Filters(context, as2Filters) {
    var arr = [];
    if (!as2Filters) {
        return arr;
    }
    var length = as2Filters.alGet('length');
    for (var i = 0; i < length; i++) {
        var filterModel = convertToAS3Filter(context, as2Filters.alGet(i));
        if (filterModel && FILTER_TO_SIMPLE_NAME[filterModel.filterType]) {
            filterModel.filterName = FILTER_TO_SIMPLE_NAME[filterModel.filterType];
            arr.push(filterModel);
        }
    }
    return arr;
}
export function convertFromTimelineFilters(context, filters) {
    if (!filters || !filters.length) {
        return undefined;
    }
    var filtersClasses = context.globals.filters;
    var result = [];
    for (var _i = 0, filters_1 = filters; _i < filters_1.length; _i++) {
        var filter = filters_1[_i];
        var filterClass = filtersClasses.alGet(FILTER_TYPE_NAME_MAP[filter.type]);
        if (!filterClass) {
            continue;
        }
        var proto = filterClass.alGetPrototypeProperty();
        if (proto.asFilterConverter) {
            var converted = proto.asFilterConverter.fromTimelineFilter(filter);
            if (!converted) {
                continue;
            }
            result.push(converted);
        }
    }
    return new AVM1ArrayNative(context, result);
}
// export function convertFromAS3Filters(context: AVM1Context, as3Filters: ASObject): AVM1Object {
// 	var arr = [];
// 	if (as3Filters) {
// 		var classes = context.globals.filters;
// 		for (var i = 0, length = as3Filters.axGetPublicProperty('length'); i < length; i++) {
// 			var as3Filter = as3Filters.axGetPublicProperty(i);
// 			// TODO inefficient search, refactor
// 			knownFilters.forEach((filterName: string) => {
// 				var filterClass = classes.alGet(filterName);
// 				var proto: AVM1BitmapFilterPrototype = filterClass.alGetPrototypeProperty();
// 				if (proto.asFilterConverter && proto.asFilterConverter.getAS3Class().axIsType(as3Filter)) {
// 					arr.push(proto.asFilterConverter.fromAS3Filter(as3Filter));
// 				}
// 			});
// 		}
// 	}
// 	return new AVM1ArrayNative(context, arr);
// }
